import { gql, useMutation } from "@apollo/client";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import BackArrow from "../../../../components/common/back-arrow";
import FadeInWrapper from "../../../../components/common/fade-in-wrapper";
import { track } from "@amplitude/analytics-browser";
import { notify, notifyError } from "../../../../utils/toast";
import { getErrorMessage } from "../../../../utils/error";

const CREATE_SELLER = gql`
  mutation createSellerRequest($contactNumber: String!, $password: String!) {
    createSellerRequest(contactNumber: $contactNumber, password: $password) {
      seller {
        id
        contactNumber
      }
      token
      ok
      error
    }
  }
`;

const SignupAccountRegister = () => {
  const navigate = useNavigate();
  const [contactNumber, setContactNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  const [contactError, setContactError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [termsError, setTermsError] = useState("");

  const [createSeller, { loading }] = useMutation(CREATE_SELLER);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!termsAgreed || !privacyAgreed) {
      setTermsError("이용약관과 개인정보 처리방침에 동의해주세요.");
      return;
    }
    setTermsError("");

    if (contactNumber.length !== 11 && contactNumber.length !== 10) {
      setContactError("연락처는 10자리 또는 11자리 숫자여야 합니다.");
      return;
    }
    setContactError("");

    if (password !== confirmPassword) {
      setConfirmPasswordError("비밀번호가 일치하지 않습니다.");
      return;
    }
    setConfirmPasswordError("");

    createSeller({
      variables: { contactNumber, password },
    })
      .then((response) => {
        const { token, ok, error } = response.data.createSellerRequest;

        if (ok) {
          localStorage.removeItem("sellerToken");
          localStorage.setItem("sellerToken", token);

          notify("계정 생성이 완료되었습니다.");
          track("사장님 입점신청5 계정 생성 완료");
          navigate("/boss/signup/store");
        } else {
          notifyError(error || "계정 생성에 실패했습니다.");
        }
      })
      .catch((err) => {
        console.error("Error during sign-up:", err);
        notifyError(getErrorMessage(err));
      });
  };

  return (
    <>
      <div className="flex items-center justify-center min-h-screen p-4">
        <FadeInWrapper className="w-full max-w-md bg-white p-6 pb-10">
          <div className="mb-4" onClick={() => navigate(-1)}>
            <BackArrow />
          </div>

          <div className="mb-6 text-center">
            <h2 className="text-2xl font-semibold text-black mb-2">계정 등록</h2>
            <p className="text-sm text-[#757575]">
              입점신청을 위해서는 우선 가입이 필요해요.
              <br />
              연락처는 아이디로 사용돼요.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="contactNumber"
                className="block text-sm font-medium text-black mb-2 pl-2"
              >
                연락처*
              </label>
              <input
                type="tel"
                id="contactNumber"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={11}
                value={contactNumber}
                onChange={(e) => {
                  const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                  if (onlyNums.length <= 11) {
                    setContactNumber(onlyNums);
                    if (contactError) setContactError("");
                  }
                }}
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary text-sm text-gray-700"
                placeholder="숫자만 입력해주세요"
                required
              />
              {contactError && (
                <p className="text-red-500 text-sm mt-1 pl-2">{contactError}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-black mb-2 pl-2"
              >
                비밀번호*
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary text-sm text-gray-700"
                placeholder="비밀번호를 입력해주세요"
                required
              />
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-black mb-2 pl-2"
              >
                비밀번호 확인*
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (confirmPasswordError) setConfirmPasswordError("");
                }}
                className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:outline-none focus:border-primary text-sm text-gray-700"
                placeholder="비밀번호를 다시 입력해주세요"
                required
              />
              {confirmPasswordError && (
                <p className="text-red-500 text-sm mt-1 pl-2">
                  {confirmPasswordError}
                </p>
              )}
            </div>

            <div className="space-y-2 ml-1">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAgreed}
                  onChange={(e) => {
                    setTermsAgreed(e.target.checked);
                    if (termsError) setTermsError("");
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="terms" className="text-sm text-gray-700">
                  <span className="text-red-500">*</span> 마감히어로{" "}
                  <button
                    type="button"
                    onClick={() => setIsTermsOpen(true)}
                    className="text-primary underline"
                  >
                    이용약관
                  </button>
                  에 동의합니다.
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={privacyAgreed}
                  onChange={(e) => {
                    setPrivacyAgreed(e.target.checked);
                    if (termsError) setTermsError("");
                  }}
                  className="w-4 h-4"
                />
                <label htmlFor="privacy" className="text-sm text-gray-700">
                  <span className="text-red-500">*</span> 마감히어로{" "}
                  <button
                    type="button"
                    onClick={() => setIsPrivacyOpen(true)}
                    className="text-primary underline"
                  >
                    개인정보 처리방침
                  </button>
                  에 동의합니다.
                </label>
              </div>
              {termsError && (
                <p className="text-red-500 text-sm mt-1 pl-2">{termsError}</p>
              )}
            </div>

            <button
              type="button"
              onClick={() => {
                setTermsAgreed(true);
                setPrivacyAgreed(true);
                setTermsError("");
              }}
              className="w-full py-3 border-2 border-primary text-primary font-semibold rounded-xl transition duration-150 active:brightness-90"
            >
              모두 동의하기
            </button>

            <button
              type="submit"
              className={`w-full py-3 rounded-xl text-white font-bold ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary hover:brightness-90 transition duration-150"
              }`}
              disabled={loading}
            >
              {loading ? "처리 중..." : "다음"}
            </button>
          </form>
        </FadeInWrapper>
      </div>

      {isTermsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg mx-4">
            <div className="flex justify-end items-center p-4">
              <button
                onClick={() => setIsTermsOpen(false)}
                className="text-gray-500"
              >
                닫기
              </button>
            </div>
            <iframe
              src="https://trapezoidal-hamburger-538.notion.site/29608e6e11b38040b202ec9c5bb6bf09"
              title="이용약관"
              className="w-full h-[80vh] rounded-b-xl"
            />
          </div>
        </div>
      )}

      {isPrivacyOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg mx-4">
            <div className="flex justify-end items-center p-4">
              <button
                onClick={() => setIsPrivacyOpen(false)}
                className="text-gray-500"
              >
                닫기
              </button>
            </div>
            <iframe
              src="https://subsequent-frog.super.site/29608e6e11b3800e8d73f172bb194d07"
              title="개인정보처리방침"
              className="w-full h-[80vh] rounded-b-xl"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default SignupAccountRegister;
